import {
  useCallback,
  useEffect,
  useState,
  type FunctionComponent,
} from "react";
import { Form } from "../../../../components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useToast } from "../../../../hooks/use-toast";
import {
  type IzCommunicationFormAdmin,
  zCommunicationFormAdmin,
} from "../../../../z/admin/communication-form.admin.z";
import { FormSelect } from "../../../../components/form-components/form-select.components";
import { FormInput } from "../../../../components/form-components/form-input.components";
import { Checkbox } from "../../../../components/ui/checkbox";
import SmallLineIcon from "../../../../assets/svgs/common/small-line.svg";
import { Button } from "../../../../components/ui/button";
import {
  debounce,
  findErrorMessage,
  handleHttpError,
} from "../../../../utils/utils";
import { FormMultiSelect } from "../../../../components/form-components/form-multiselect.components";
import {
  type ISocial,
  SocialApplicationStatus,
  SocialCreatorType,
  SocialGender,
} from "../../../../types/models/social.models.types";
import { Separator } from "../../../../components/ui/separator";
import BellIcon from "../../../../assets/svgs/common/bell.svg";
import SearchIcon from "../../../../assets/svgs/common/search.svg";
import { Input } from "../../../../components/ui/input";
import { XIcon } from "lucide-react";
import Spinner from "../../../../components/spinner.components";
import { useSearchSocialsAdminHttp } from "../../../../hooks/http/admin/socials/search-socials.admin.http";
import { useSendBulkAppNotificationAdminHttp } from "../../../../hooks/http/admin/notifications/send-bulk-app-notification.admin.http";
import { SendTestNotificationButton } from "../../../../components/action-buttons/communication/send-test.component";
import { ScrollArea } from "../../../../components/ui/scroll-area";
import { useNavigate } from "react-router-dom";

export const NewCommunicationSliderAdminPartial: FunctionComponent = () => {
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [disabled, setDisabled] = useState(true);
  const [formStage, setFormStage] = useState({
    stage1: { open: true, completed: false },
    stage2: { open: false, completed: false },
    stage3: { open: false, completed: false },
  });
  const [socials, setSocials] = useState<ISocial[]>([]);
  const [typing, setTyping] = useState(false);
  const navigate = useNavigate();

  const {
    execute: searchSocialHttp,
    fetching: searchingSocials,
    cancel: cancelSearchSocialHttp,
  } = useSearchSocialsAdminHttp();

  const {
    execute: sendBulkAppNotificationHttp,
    fetching: sendingBulkAppNotification,
  } = useSendBulkAppNotificationAdminHttp();

  const debouncedSearch = useCallback(
    debounce(async (searchValue: string) => {
      const searchString = searchValue.trim();
      if (!searchString) {
        return;
      }
      const response = await searchSocialHttp({
        body: null,
        queryParams: {
          searchString: searchString,
          size: 4,
        },
      });
      if (!response.success) {
        return handleHttpError(response, toast);
      }
      setSocials(response.data.socials);
      setTyping(false);
    }, 500),
    []
  );

  function handleSearch(e: React.ChangeEvent<HTMLInputElement>) {
    cancelSearchSocialHttp();
    setSearch(e.target.value);
    debouncedSearch(e.target.value);
    setTyping(true);
    if (!e.target.value.trim().length) {
      setTyping(false);
      setSocials([]);
    }
  }

  const form = useForm<IzCommunicationFormAdmin>({
    resolver: zodResolver(zCommunicationFormAdmin),
    defaultValues: {
      title: "",
      description: "",
      notificationType: "fcm",
      filters: {
        socials: {
          creatorType: [],
          genderPreference: [],
          applicationStatus: [],
        },
        // campaign: {
        // 	campaignId: "",
        // 	postStatus: [],
        // },
      },
    },
  });

  function handleProceed() {
    console.log("cannot function", disabled);
    if (!disabled) {
      setFormStage((prevStage) => {
        if (prevStage.stage1.open) {
          return {
            ...prevStage,
            stage1: { open: false, completed: true },
            stage2: { open: true, completed: false },
          };
        }
        if (prevStage.stage2.open) {
          return {
            ...prevStage,
            stage2: { open: false, completed: true },
            stage3: { open: true, completed: false },
          };
        }

        return prevStage;
      });
    }
  }

  function handlePrevious() {
    if (formStage.stage2.open) {
      setFormStage({
        ...formStage,
        stage1: { open: true, completed: false },
        stage2: { open: false, completed: false },
      });
    } else if (formStage.stage3.open) {
      setSocials([]);
      setFormStage({
        ...formStage,
        stage2: { open: true, completed: false },
        stage3: { open: false, completed: false },
      });
    }
  }

  const onSubmit = async (data: IzCommunicationFormAdmin) => {
    console.log(data);
    const response = await sendBulkAppNotificationHttp({
      body: data,
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }
    toast({
      content: "Notification sent successfully",
      toastType: "success",
    });
    navigate("/admin/communication");
    document.getElementById("getPastNotifications")?.click();
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (form.formState.errors) {
      const key = Object.keys(form.formState.errors)[0];

      if (key) {
        const error =
          form.formState.errors[key as keyof typeof form.formState.errors];
        const message = findErrorMessage(error) || "An error occurred";

        toast({
          content: message,
          toastType: "error",
        });
      }
    }
  }, [form.formState.errors]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    const canProceedStage1 =
      formStage.stage1.open &&
      form.getValues("notificationType") &&
      form.getValues("title") &&
      form.getValues("description");

    const canProceedStage2 = formStage.stage2.open; // Adjust if you need more validation here.
    console.log("currentvalue", disabled);
    if (canProceedStage1 || canProceedStage2) {
      setDisabled(false);
      console.log("cannot proceed", false);
    } else {
      setDisabled(true);
      console.log("cannot proceed", true);
    }
  }, [
    formStage.stage1.open,
    formStage.stage2.open,
    form.watch("notificationType"),
    form.watch("title"),
    form.watch("description"),
  ]);

  function handleFields() {
    if (formStage.stage1.open) {
      return (
        <div className="flex flex-col gap-10 pt-4 w-full">
          <FormSelect
            form={form}
            label="Communication medium"
            name="notificationType"
            options={[{ label: "App Notification", value: "fcm" }]}
            placeholder="Select"
          />

          <FormInput
            form={form}
            label="Title"
            name="title"
            placeholder="Enter title"
            type="text"
            disabled={form.getValues("notificationType").length === 0}
          />

          <FormInput
            form={form}
            label="Description"
            name="description"
            placeholder="Enter description"
            type="text"
            disabled={form.getValues("notificationType").length === 0}
          />
        </div>
      );
    }
    if (formStage.stage2.open) {
      return (
        <div className="flex flex-col gap-10 pt-4 w-full">
          <FormMultiSelect
            form={form}
            label="Creator type"
            name="filters.socials.creatorType"
            options={[
              { value: SocialCreatorType.face, label: "Face", icon: "😃" },
              { value: SocialCreatorType.meme, label: "Meme", icon: "🫠" },
            ]}
            placeholder="Select"
          />

          <FormMultiSelect
            form={form}
            label="Social status"
            name="filters.socials.applicationStatus"
            options={[
              { value: SocialApplicationStatus.applied, label: "Applied" },
              { value: SocialApplicationStatus.approved, label: "Approved" },
              { value: SocialApplicationStatus.rejected, label: "Rejected" },
            ]}
            placeholder="Select"
          />

          <FormMultiSelect
            form={form}
            label="Gender"
            name="filters.socials.genderPreference"
            options={[
              { value: SocialGender.male, label: "Male" },
              { value: SocialGender.female, label: "Female" },
              { value: SocialGender.other, label: "Other" },
              { value: SocialGender.prefernotsay, label: "Prefer not to say" },
            ]}
            placeholder="Select"
          />
        </div>
      );
    }
    if (formStage.stage3.open) {
      return (
        <div className="flex flex-col gap-8 w-full">
          <p>Send test communication</p>
          <div className="relative flex items-center bg-bginput py-2 pl-5 border border-border rounded-10">
            <SearchIcon />
            <Input
              value={search}
              onChange={(e) => handleSearch(e)}
              className="bg-transparent border-none w-96 font-semibold text-base placeholder:text-secondary"
              placeholder="Search by name, Instagram ID, and number"
            />
            {search.trim().length > 0 && (
              <XIcon
                onClick={() => {
                  setSearch("");
                  setSocials([]);
                }}
                className="right-5 absolute cursor-pointer"
              />
            )}
          </div>
          <ScrollArea className="flex flex-col px-4 h-80 overflow-scroll">
            {searchingSocials || typing ? (
              <div className="flex justify-center items-center w-full h-56">
                <Spinner width={20} />
              </div>
            ) : !socials.length && search.length > 0 ? (
              <div className="flex justify-center items-center w-full h-56">
                <p>No socials found</p>
              </div>
            ) : (
              socials.map((social) => {
                return (
                  <div
                    key={social.id}
                    className="flex justify-between items-center mb-6"
                  >
                    <div className="flex items-center gap-4">
                      <div className="border-2 rounded-full w-8 h-8">
                        <img
                          src={social.profileImage || "/static/profile.png"}
                          alt=""
                        />
                      </div>
                      <p>{social.handle}</p>
                    </div>
                    <SendTestNotificationButton
                      title={form.getValues("title")}
                      description={form.getValues("description")}
                      social={social}
                    />
                  </div>
                );
              })
            )}
          </ScrollArea>
        </div>
      );
    }
  }

  return (
    <div className="relative flex flex-col gap-10 w-full h-full">
      <div className="px-7 w-full">
        <p className="text-xl">New communication</p>
      </div>

      <div className="flex flex-col">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            onError={(formState) => console.log("Form error:", formState)}
            className="relative gap-8 grid grid-cols-1"
          >
            <div className="flex justify-between items-center px-7 py-4 w-full">
              <div className="flex flex-col items-center gap-1">
                <Checkbox
                  className={`${formStage.stage1.completed ? "" : "data-[state=checked]:bg-primarybuttonbg "}border-border rounded-full`}
                  checked={formStage.stage1.completed || formStage.stage1.open}
                />
                <p
                  className={
                    formStage.stage1.completed
                      ? "text-primary"
                      : formStage.stage1.open
                        ? "text-primarybuttonbg"
                        : "text-tertiary"
                  }
                >
                  Content
                </p>
              </div>
              <SmallLineIcon
                className={
                  formStage.stage2.open ||
                  formStage.stage3.open ||
                  formStage.stage2.completed ||
                  formStage.stage3.completed
                    ? "text-primary"
                    : "text-tertiary"
                }
              />
              <div className="flex flex-col items-center gap-1">
                <Checkbox
                  className={`${formStage.stage2.completed ? "" : "data-[state=checked]:bg-primarybuttonbg "}border-border rounded-full`}
                  checked={formStage.stage2.completed || formStage.stage2.open}
                />
                <p
                  className={
                    formStage.stage2.completed
                      ? "text-primary"
                      : formStage.stage2.open
                        ? "text-primarybuttonbg"
                        : "text-tertiary"
                  }
                >
                  Filter
                </p>
              </div>
              <SmallLineIcon
                className={
                  formStage.stage3.open || formStage.stage3.completed
                    ? "text-primary"
                    : "text-tertiary"
                }
              />
              <div className="flex flex-col items-center gap-1">
                <Checkbox
                  className={`${formStage.stage3.completed ? "" : "data-[state=checked]:bg-primarybuttonbg "}border-border rounded-full`}
                  checked={formStage.stage3.completed || formStage.stage3.open}
                />
                <p
                  className={
                    formStage.stage3.completed
                      ? "text-primary"
                      : formStage.stage3.open
                        ? "text-primarybuttonbg"
                        : "text-tertiary"
                  }
                >
                  Send
                </p>
              </div>
            </div>
            <Separator />
            <div className="px-7">{handleFields()}</div>

            <Button id="sendNotification" className="hidden" type="submit">
              Submit
            </Button>
          </form>
        </Form>
      </div>
      <div className="bottom-0 absolute flex items-start gap-5 px-7 w-full">
        {(formStage.stage2.open || formStage.stage3.open) && (
          <Button
            onClick={() => {
              handlePrevious();
            }}
            className="bg-previousbuttonbg py-5 border border-previousbuttonborder w-full font-semibold text-previousbuttontext"
          >
            Previous
          </Button>
        )}

        {(formStage.stage1.open || formStage.stage2.open) && (
          <Button
            disabled={disabled}
            onClick={handleProceed}
            className="bg-approvebuttonbg py-5 border border-approvebuttonborder w-full font-semibold text-approvebuttontext"
          >
            Proceed →
          </Button>
        )}
        {formStage.stage3.open && (
          <Button
            disabled={sendingBulkAppNotification}
            onClick={() => {
              form.handleSubmit(onSubmit)();
            }}
            className="bg-approvebuttonbg py-5 border border-approvebuttonborder w-full font-semibold text-approvebuttontext"
          >
            {sendingBulkAppNotification ? (
              <Spinner width={20} />
            ) : (
              <div className="flex items-center gap-2">
                Send notification <BellIcon />
              </div>
            )}
          </Button>
        )}
      </div>
    </div>
  );
};
